import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../lib/utils';
import { 
  Calculator, 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Receipt,
  CreditCard
} from 'lucide-react';

const ProjectProfitOverview = ({ project }) => {
  // Parse JSON fields safely
  const vat = (() => {
    try {
      return typeof project?.vat === 'string' ? JSON.parse(project.vat) : project?.vat;
    } catch (e) {
      return { enabled: true, percentage: 7, amount: 0 };
    }
  })();

  const withholdingTax = (() => {
    try {
      return typeof project?.withholding_tax === 'string' ? JSON.parse(project.withholding_tax) : project?.withholding_tax;
    } catch (e) {
      return { enabled: true, percentage: 3, amount: 0 };
    }
  })();

  const companyFee = (() => {
    try {
      return typeof project?.company_fee === 'string' ? JSON.parse(project.company_fee) : project?.company_fee;
    } catch (e) {
      return { type: 'percentage', percentage: 0, amount: 0 };
    }
  })();

  const discount = (() => {
    try {
      return typeof project?.discount === 'string' ? JSON.parse(project.discount) : project?.discount;
    } catch (e) {
      return { enabled: false, type: 'percentage', percentage: 0, amount: 0 };
    }
  })();

  // ใช้ค่าจากฐานข้อมูลแทนการคำนวณเอง
  const projectPrice = project?.project_price || 0;
  const priceBeforeVat = project?.price_before_vat || 0;
  const totalWithVat = project?.total_with_vat || 0;
  const vatAmount = vat?.amount || 0;
  const whtAmount = withholdingTax?.amount || 0;
  const actualReceived = project?.actual_received || 0;
  
  // คำนวณส่วนลด (ยังคงใช้การคำนวณเพราะไม่มีในฐานข้อมูล)
  const discountAmount = discount.enabled 
    ? (discount.type === 'percentage' 
        ? (projectPrice * (discount.percentage || 0)) / 100
        : (discount.amount || 0))
    : 0;

  // คำนวณค่าใช้จ่าย
  const paidDevPayments = project?.developer_payments?.reduce((sum, dev) => 
    sum + (dev.paid ? Number(dev.amount || 0) : 0), 0) || 0;
  const paidOtherExpenses = project?.other_expenses?.reduce((sum, expense) => 
    sum + (expense.paid ? Number(expense.amount || 0) : 0), 0) || 0;
  const salesCommission = project?.total_sales_commission_amount || project?.totalSalesCommissionAmount || 0;
  
  // ค่าใช้จ่ายรวม (ก่อน WHT)
  const totalExpensesBeforeWht = paidDevPayments + paidOtherExpenses + salesCommission;
  
  // คำนวณ WHT ที่หักจากค่าใช้จ่าย (สมมติว่า Developer มี WHT 3%)
  const devWhtAmount = project?.developer_payments?.reduce((sum, dev) => {
    if (dev.paid && dev.withholding_tax) {
      const devWht = typeof dev.withholding_tax === 'string' ? JSON.parse(dev.withholding_tax) : dev.withholding_tax;
      return sum + (devWht.amount || 0);
    }
    return sum;
  }, 0) || 0;
  
  // ค่าใช้จ่ายหลังหัก WHT
  const totalExpensesAfterWht = totalExpensesBeforeWht - devWhtAmount;
  
  // กำไรสุทธิ = รายได้รับจริง - ค่าใช้จ่ายหลังหัก WHT
  const netProfit = actualReceived - totalExpensesAfterWht;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Calculator className="h-5 w-5" />
          <span>สรุปกำไรขาดทุน</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* รายรับ */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <span>รายรับ</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* ราคาโปรเจค */}
              <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">ราคาโปรเจค</span>
                  </div>
                  <span className="text-lg font-bold text-slate-800">
                    {formatCurrency(projectPrice)}
                  </span>
                </div>
              </div>

              {/* ส่วนลด */}
              {discount.enabled && discountAmount > 0 && (
                <div className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <TrendingDown className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-700">
                        ส่วนลด {discount.type === 'percentage' ? `(${discount.percentage}%)` : ''}
                      </span>
                    </div>
                    <span className="text-lg font-bold text-yellow-800">
                      -{formatCurrency(discountAmount)}
                    </span>
                  </div>
                </div>
              )}

              {/* ราคาหลังหักส่วนลด */}
              {discount.enabled && discountAmount > 0 && (
                <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg border border-emerald-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Calculator className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-700">ราคาหลังหักส่วนลด</span>
                    </div>
                    <span className="text-lg font-bold text-emerald-800">
                      {formatCurrency(projectPrice - discountAmount)}
                    </span>
                  </div>
                </div>
              )}

              {/* ราคารวม VAT */}
              <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">ราคารวม VAT</span>
                  </div>
                  <span className="text-lg font-bold text-blue-800">
                    {formatCurrency(totalWithVat)}
                  </span>
                </div>
              </div>

              {/* VAT */}
              <div className="p-4 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg border border-cyan-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Receipt className="h-4 w-4 text-cyan-600" />
                    <span className="text-sm font-medium text-cyan-700">VAT ({vat.percentage || 0}%)</span>
                  </div>
                  <span className="text-lg font-bold text-cyan-800">
                    {formatCurrency(vatAmount)}
                  </span>
                </div>
              </div>

              {/* ราคาก่อน VAT */}
              <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Calculator className="h-4 w-4 text-indigo-600" />
                    <span className="text-sm font-medium text-indigo-700">ราคาก่อน VAT</span>
                  </div>
                  <span className="text-lg font-bold text-indigo-800">
                    {formatCurrency(priceBeforeVat)}
                  </span>
                </div>
              </div>

              {/* WHT (ขารับ) */}
              <div className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border border-yellow-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-700">WHT ขารับ ({withholdingTax.percentage || 0}%)</span>
                  </div>
                  <span className="text-lg font-bold text-yellow-800">
                    {formatCurrency(whtAmount)}
                  </span>
                </div>
              </div>

              {/* ราคารับจริง */}
              <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">ราคารับจริง</span>
                  </div>
                  <span className="text-lg font-bold text-green-800">
                    {formatCurrency(actualReceived)}
                  </span>
                </div>
                <div className="text-xs text-green-600 mt-1">
                  ราคารวม VAT - WHT
                </div>
              </div>
            </div>
          </div>

          {/* รายจ่าย */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center space-x-2">
              <TrendingDown className="h-5 w-5 text-red-600" />
              <span>รายจ่าย</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* ค่าใช้จ่าย Developer */}
              <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Receipt className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-700">Developer</span>
                  </div>
                  <span className="text-lg font-bold text-orange-800">
                    {formatCurrency(paidDevPayments)}
                  </span>
                </div>
              </div>

              {/* ค่าใช้จ่ายอื่นๆ */}
              <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Receipt className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700">ค่าใช้จ่ายอื่นๆ</span>
                  </div>
                  <span className="text-lg font-bold text-purple-800">
                    {formatCurrency(paidOtherExpenses + salesCommission)}
                  </span>
                </div>
              </div>

              {/* ค่าใช้จ่ายรวม (ก่อน WHT) */}
              <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <TrendingDown className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium text-red-700">ค่าใช้จ่ายรวม (ก่อน WHT)</span>
                  </div>
                  <span className="text-lg font-bold text-red-800">
                    {formatCurrency(totalExpensesBeforeWht)}
                  </span>
                </div>
              </div>

              {/* WHT ขาจ่าย */}
              <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-700">WHT ขาจ่าย</span>
                  </div>
                  <span className="text-lg font-bold text-amber-800">
                    {formatCurrency(devWhtAmount)}
                  </span>
                </div>
              </div>

              {/* ค่าใช้จ่ายหลังหัก WHT */}
              <div className="p-4 bg-gradient-to-br from-rose-50 to-rose-100 rounded-lg border border-rose-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <TrendingDown className="h-4 w-4 text-rose-600" />
                    <span className="text-sm font-medium text-rose-700">ค่าใช้จ่ายหลังหัก WHT</span>
                  </div>
                  <span className="text-lg font-bold text-rose-800">
                    {formatCurrency(totalExpensesAfterWht)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* กำไรสุทธิ & ค่าบริการบริษัท */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 flex items-center space-x-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              <span>กำไรสุทธิ & ค่าบริการบริษัท</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* กำไรสุทธิ */}
              <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                  <span className="text-lg font-semibold text-blue-700">กำไรสุทธิ</span>
                </div>
                <p className="text-3xl font-bold text-blue-800 mb-2">
                  {formatCurrency(netProfit)}
                </p>
                <div className="text-sm text-blue-600">
                  <p>รายได้รับจริง - ค่าใช้จ่ายหลังหัก WHT</p>
                  <p className="mt-1">
                    {formatCurrency(actualReceived)} - {formatCurrency(totalExpensesAfterWht)} = {formatCurrency(netProfit)}
                  </p>
                </div>
              </div>

              {/* ค่าบริการบริษัท */}
              <div className="p-6 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Calculator className="h-6 w-6 text-indigo-600" />
                  <span className="text-lg font-semibold text-indigo-700">ค่าบริการบริษัท</span>
                </div>
                <p className="text-3xl font-bold text-indigo-800 mb-2">
                  {formatCurrency(companyFee.amount || 0)}
                </p>
                <div className="text-sm text-indigo-600">
                  <p>ประเภท: {companyFee.type === 'percentage' ? 'เปอร์เซ็นต์' : 'จำนวนเงิน'}</p>
                  {companyFee.type === 'percentage' && (
                    <p className="mt-1">({companyFee.percentage || 0}%)</p>
                  )}
                  {companyFee.installment_distribution && companyFee.installment_distribution.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium">ผูกกับงวด:</p>
                      <div className="flex flex-wrap gap-1 mt-1 justify-center">
                        {companyFee.installment_distribution.map((dist, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 bg-indigo-200 text-indigo-800 rounded text-xs"
                          >
                            งวด {dist.installment} ({dist.percentage || 0}%)
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectProfitOverview;